import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { convertKeysToSnakeCase } from "@shared/utils/caseConverter";
import React, { useEffect, useState } from "react";
import { toast } from "@shared/ui/use-toast";
import {
  useActiveMeterReadingStatuses,
  usePostMeterReasonCodes,
  useUpdateMeterReasonCodes,
} from "../../hooks";
import { logEvent } from "@shared/analytics/analytics";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";

interface ReasonCodesAddEditFormProps {
  open: boolean;
  onOpenChange: () => void;
  initialData?: any;
}
const ReasonCodesAddEditForm: React.FC<ReasonCodesAddEditFormProps> = ({
  open,
  onOpenChange,
  initialData,
}) => {
  const { data: meterStatus } = useChoices({
    choice: "meter_meter_reading_status",
  });
  const { mutate: createStatus } = usePostMeterReasonCodes();
  const { mutate: updateStatus } = useUpdateMeterReasonCodes();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: activeStatuses } =
    useActiveMeterReadingStatuses(remoteUtilityId);
  const [isEditing, setIsEditing] = useState(false);
  const activeStatusList = mapToLabelValue(activeStatuses, "name", "id");
  const {startTracking, stopTracking} = useFormCompletionTracker('MX Reason Codes');

  const fields: FormField[] = [
    {
      name: "meterReadingStatus",
      type: "select",
      disabled: isEditing,
      label: "Reading Status",
      required: true,
      placeholder: "Select status",
      options: activeStatusList,
    },
    {
      name: "reason",
      type: "text",
      label: "Reason",
      required: true,
      placeholder: "Enter reason",
      maxLength: 50,
    },
    {
      name: "reasonCode",
      type: "textarea",
      label: "Reason Code",
      placeholder: "Enter reason code",
      rows: 3,
      maxLength: 50,
    },
  ];
  const formConfig: FormConfig = {
    fields,
    showResetButton: true,
    showSubmitButton: true,
    submitButtonText: "Submit",
    resetButtonText: "Cancel",
    mode: "onChange",
    validateOnMount: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };
  const form = FormService.createForm(formConfig);
  const handleSubmit = async (data: any) => {
    try {
      const isValid = await FormService.validateForm(form);
      if (!isValid) {
        return;
      }
      console.log("Submitted:", data);
      const payload = {
        ...data,
        remoteUtilityId,
      };
      const snakeCasePayload = convertKeysToSnakeCase(payload);
      if (isEditing) {
        updateStatus(
          {
            id: initialData.id,
            payload: snakeCasePayload,
          },
          {
            onSuccess: () => {
toast({
  title: "Success",
  description: "Reason code updated successfully.",
  variant: "default",
});            },
            onError: () => {
toast({
  title: "Error",
  description: "Something went wrong.",
  variant: "destructive",
});            },
          }
        );
      } else {
        createStatus(snakeCasePayload, {
          onSuccess: () => {
toast({
  title: "Success",
  description: "Reason added successfully",
  variant: "default",
});          },
          onError: () => {
toast({
  title: "Error",
  description: "Something went wrong",
  variant: "destructive",
});          },
        });
      }
      onOpenChange();
      setIsEditing(false);
    } catch (error) {
      console.error("Validation failed or unexpected error:", error);
toast({
  title: "Error",
  description: "Validation failed or something went wrong.",
  variant: "destructive",
});
    }
  };
  const handleCancel = () => {
    onOpenChange();
    fields.map((field) => {
      form.setValue(field.name, "");
    });
    setIsEditing(false);
  };
  useEffect(() => {
    startTracking();
    if (initialData && open) {
      setIsEditing(true);
      const flattenedData = {
        ...initialData,
        meterReadingStatus: initialData.meterReadingStatus?.id,
      };
      FormService.populateForm(form, flattenedData, fields);
    } else {
      FormService.populateForm(form, "", fields);
    }
  }, [initialData, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reading Status</DialogTitle>
        </DialogHeader>
        <DynamicForm
          form={form}
          fields={fields}
          config={formConfig}
          gridColumns={1}
          onSubmit={handleSubmit}
          onReset={handleCancel}
        />
      </DialogContent>
    </Dialog>
  );
};
export default ReasonCodesAddEditForm;
